import { createStyles, Divider, Grid } from '@material-ui/core';
import { makeStyles, styled } from '@material-ui/core/styles';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { Dialog, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useMemo, useState } from 'react';

import { IInspectorListBasedOnOrganization } from '../../models/models';
import useSelectedWpInspectors from '../../rxdb/viewInspector/useSelectedWpInspectors';
import { FieldInspectorType } from '../../utilities/enums';
import CustomButton from '../CustomButton';
import StyledButton from './StyledButton';

const ToggleButton = styled(MuiToggleButton)(({}) => ({
  '&.Mui-selected:not(.Mui-disabled), &.Mui-selected:hover:not(.Mui-disabled)': {
    color: '#121212',
    backgroundColor: 'rgba(42, 171, 63, 1)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#121212',
    backgroundColor: 'dimgray',
  },
}));

const useStyles = makeStyles(() =>
  createStyles({
    locationButtonGrid: {
      display: 'flex',
      flexFlow: 'wrap',
    },
    dividerGrid: {
      '& > hr ': {
        backgroundColor: 'rgba(255, 255, 255, 0.48)',
      },
    },
    divider: {
      width: '95%',
      margin: '8px 0px',
      color: '#E0E0E0',
    },
    orgNameLabelGrid: {
      '& > p': {
        fontSize: '14px',
        letterSpacing: '0.25px',
      },
    },
    nameLabel: {
      paddingTop: '4px',
    },
    outerGridOfDialog: {
      padding: '6px 0px',
      maxHeight: 200,
      overflowY: 'auto',
      width: '-webkit-fill-available',
    },
    messageGrid: {
      '& > p ': {
        margin: '20px 0px',
      },
    },
    message: {
      textAlign: 'center',
    },
  }),
);

interface Props {
  open: boolean;
  onClose: () => void;
  workPackageId: number;
}

const InspectorInfo: React.FC<Props> = ({ open, onClose, workPackageId }) => {
  const classes = useStyles();
  const [inspectorType, setInspectorType] = useState<string>(FieldInspectorType.FI);
  const [disableToggleButton] = useState(false);
  const [isDECView, setIsDECView] = useState(false);
  const { fieldInspectorsOnWorkPackage: inspectorData } = useSelectedWpInspectors(workPackageId);

  useEffect(() => {
    const isDECViewSelected = inspectorType === FieldInspectorType.DEC;
    setIsDECView(isDECViewSelected);
  }, [inspectorType]);

  const handleToggleChange = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      setInspectorType(inspType);
    }
  };

  const filteredInspectorData = useMemo(() => {
    return inspectorData?.filter(item => {
      if (isDECView) {
        return item.clerkId;
      } else {
        return item.fieldInspectorId;
      }
    });
  }, [inspectorData, isDECView]);

  const arrayOfAllInspectors = useMemo(() => {
    const data: IInspectorListBasedOnOrganization = {};
    filteredInspectorData?.forEach(inspector => {
      if (data[inspector?.organizationName as string]) {
        data[inspector?.organizationName as string].push(
          inspector.fieldInspectorName ? inspector.fieldInspectorName : inspector.clerkName || '',
        );
      } else {
        data[inspector?.organizationName as string] = [
          inspector.fieldInspectorName ? inspector.fieldInspectorName : inspector.clerkName || '',
        ];
      }
    });
    return data;
  }, [filteredInspectorData]);

  const getDivider = () => {
    return <Divider orientation="horizontal" className={classes.divider} />;
  };

  return (
    <Dialog
      open={open}
      titleText={`${!isDECView ? 'Field Inspectors' : 'Data Entry Clerks'}`}
      onClose={onClose}
      backButton={false}
      dialogActions={<CustomButton buttonText="Ok" onClose={onClose} />}
      maxWidth="xs"
      noMinHeight
    >
      <Grid item container data-testid="inspector-info-grid">
        <ToggleButtonGroup
          color="#121212"
          value={inspectorType}
          exclusive
          onChange={handleToggleChange}
        >
          <ToggleButton
            disabled={disableToggleButton}
            value={FieldInspectorType.FI}
            data-testid="nutrientogglebutton"
          >
            Field Inspector
          </ToggleButton>
          <ToggleButton
            disabled={disableToggleButton}
            value={FieldInspectorType.DEC}
            data-testid="sptogglebutton"
          >
            Data Entry Clerk
          </ToggleButton>
        </ToggleButtonGroup>
        {getDivider()}
        {Object.keys(arrayOfAllInspectors)?.length > 0 && (
          <Grid className={classes.outerGridOfDialog}>
            {Object.keys(arrayOfAllInspectors)?.map((orgName: string, index: number) => {
              return (
                <>
                  <Grid item container alignItems="center" className={classes.orgNameLabelGrid}>
                    <Typography variant="body2" className={classes.nameLabel}>
                      {orgName}
                    </Typography>
                  </Grid>
                  <Grid item className={classes.locationButtonGrid}>
                    {arrayOfAllInspectors[orgName].map((inspector: string) => {
                      return <StyledButton inspector={inspector} canDelete={false} />;
                    })}
                  </Grid>
                  {index + 1 !== Object.keys(arrayOfAllInspectors).length && (
                    <Grid className={classes.dividerGrid}>{getDivider()}</Grid>
                  )}
                </>
              );
            })}
          </Grid>
        )}

        {inspectorData && inspectorData?.length === 0 && (
          <Grid
            className={classes.messageGrid}
            container
            item
            xs={12}
            justifyContent="center"
            alignContent="flex-start"
          >
            <Typography variant="body1" className={classes.message}>
              Field Inspectors are not assigned
            </Typography>
          </Grid>
        )}
      </Grid>
    </Dialog>
  );
};

export default InspectorInfo;
